import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import t from "../../../app/i18n";
import { financialMediationApi } from "../../../modules/financialmediation/api";
import {
  AssignFinancialMediation,
  ChangeFinancialMediationVersion,
  CreateUpdateFinancialMediation,
  FillFinancialMediation,
  FinancialMediationFilterPageRequest,
} from "../../../modules/financialmediation/types";
import { UUID } from "../../../typings/global";
import { EntityObject } from "../../types";

export const FINANCIAL_MEDIATION_QUERY_KEYS = {
  filterFinancialMediations: (payload?: FinancialMediationFilterPageRequest) =>
    payload ? ["filterFinancialMediations", payload] : ["filterFinancialMediations"],
  getFinancialMediation: (id: UUID) => ["getFinancialMediation", id],
  createFinancialMediation: () => ["createFinancialMediation"],
  updateFinancialMediation: () => ["updateFinancialMediation"],
  deleteFinancialMediation: () => ["deleteFinancialMediation"],
  changeFinancialMediationVersion: () => ["changeFinancialMediationVersion"],
  assignFinancialMediation: () => ["assignFinancialMediation"],
  fillFinancialMediation: () => ["fillFinancialMediation"],
};

export const useFilterFinancialMediations = (payload: FinancialMediationFilterPageRequest) => {
  return useQuery({
    queryKey: FINANCIAL_MEDIATION_QUERY_KEYS.filterFinancialMediations(payload),
    queryFn: async () => {
      const { data } = await financialMediationApi.filterFinancialMediations(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetFinancialMediation = (id?: UUID) => {
  return useQuery({
    queryKey: FINANCIAL_MEDIATION_QUERY_KEYS.getFinancialMediation(id!),
    queryFn: async () => {
      const { data } = await financialMediationApi.getFinancialMediation({ id: id! });

      return data;
    },
    enabled: !!id,
  });
};

export const useCreateFinancialMediation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: FINANCIAL_MEDIATION_QUERY_KEYS.createFinancialMediation(),
    mutationFn: async (payload: CreateUpdateFinancialMediation) => {
      const { data } = await financialMediationApi.createFinancialMediation(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FINANCIAL_MEDIATION_QUERY_KEYS.filterFinancialMediations() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateFinancialMediation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: FINANCIAL_MEDIATION_QUERY_KEYS.updateFinancialMediation(),
    mutationFn: async (payload: EntityObject<CreateUpdateFinancialMediation>) => {
      const { data } = await financialMediationApi.updateFinancialMediation(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: FINANCIAL_MEDIATION_QUERY_KEYS.filterFinancialMediations() });
      queryClient.setQueryData(FINANCIAL_MEDIATION_QUERY_KEYS.getFinancialMediation(data.id), () => data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteFinancialMediation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: FINANCIAL_MEDIATION_QUERY_KEYS.deleteFinancialMediation(),
    mutationFn: async (id: UUID) => {
      return financialMediationApi.deleteFinancialMediation({ id });
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: FINANCIAL_MEDIATION_QUERY_KEYS.filterFinancialMediations() });
      queryClient.removeQueries({ queryKey: FINANCIAL_MEDIATION_QUERY_KEYS.getFinancialMediation(id) });
      Notification.itemDeletedNotification();
    },
  });
};

export const useChangeFinancialMediationVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: FINANCIAL_MEDIATION_QUERY_KEYS.changeFinancialMediationVersion(),
    mutationFn: async (payload: EntityObject<ChangeFinancialMediationVersion>) => {
      const { data } = await financialMediationApi.changeFinancialMediationVersion(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: FINANCIAL_MEDIATION_QUERY_KEYS.filterFinancialMediations() });
      queryClient.setQueryData(FINANCIAL_MEDIATION_QUERY_KEYS.getFinancialMediation(data.id), () => data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useAssignFinancialMediation = ({ globalSpinner = true } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: FINANCIAL_MEDIATION_QUERY_KEYS.assignFinancialMediation(),
    meta: { globalSpinner },
    mutationFn: async (payload: EntityObject<AssignFinancialMediation>) => {
      const { data } = await financialMediationApi.assignFinancialMediation(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: FINANCIAL_MEDIATION_QUERY_KEYS.filterFinancialMediations() });
      queryClient.setQueryData(FINANCIAL_MEDIATION_QUERY_KEYS.getFinancialMediation(data.id), () => data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useFillFinancialMediation = () => {
  return useMutation({
    mutationKey: FINANCIAL_MEDIATION_QUERY_KEYS.fillFinancialMediation(),
    mutationFn: async (payload: FillFinancialMediation) => {
      const response = await financialMediationApi.fillFinancialMediation(payload);
      openBlobFile(response, true);

      return response.data;
    },
    onSuccess: () => {
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("common.createOperationSuccess"),
      });
    },
  });
};
